import React, { useContext, useState } from 'react';

import { compose } from 'redux';

import { reloadFreshdeskSettings } from 'common/actions/freshdeskSettings';
import AJAX from 'common/AJAX';
import { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import TextInput from 'common/inputs/TextInput';
import LazyLoadedImage from 'common/LazyLoadedImage';
import ModernModal from 'common/modals/ModernModal';
import AdminAutopilotInfoBox from 'common/subdomain/admin/AdminQueue/GettingStarted/AdminAutopilotInfoBox';
import { InfoPoints } from 'common/subdomain/admin/AdminQueue/GettingStarted/constants';
import ButtonV2 from 'common/ui/ButtonV2';
import Label from 'common/ui/common/Label';
import { H2, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import validateInput from 'common/validateInput';
import styles from 'css-module/components/subdomain/admin/AdminQueue/GettingStarted/_AdminAutopilotIntegrationModal.module.scss';

import CannyLogo from 'img/canny-icon.svg';

import type { Props as ModalProps } from './AdminAutopilotIntegrationModal';
import type { Company } from 'common/api/endpoints/companies';

type OwnProps = Omit<ModalProps, 'getOAuthURL' | 'connectDisabled' | 'inputSection'>;

type ConnectProps = {
  reloadFreshdeskSettings: () => Promise<void>;
};

const FreshdeskIntegrationModal = ({
  onClose,
  integration,
  enableIntegrationSync,
  reloadFreshdeskSettings,
}: OwnProps & ConnectProps) => {
  // context
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);

  // state
  const [domain, setDomain] = useState('');
  const [apiKey, setAPIKey] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  // helpers
  const onEnableClick = async () => {
    const success = isInstalled || (await install());
    if (success) {
      enableIntegrationSync(integration);
      onClose();
    }
  };

  const isInstalled = company.activeIntegrations?.freshdesk;
  const isValid =
    validateInput.freshdesk.domain(domain) && validateInput.string(apiKey) && apiKey.length;

  const install = async () => {
    setLoading(true);

    const response = await AJAX.post('/api/queue/installAndEnable', {
      integrationName: SyncIntegrationNames.freshdesk,
      conversationSource: {
        code: apiKey,
        subdomain: domain,
      },
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        'not allowed':
          "You don't have permissions to install the Freshdesk integration. Please ask one of your team members with Freshdesk Account Admin privileges for help, or contact support for more details.",
        'invalid domain': 'Please enter a valid Freshdesk domain',
        'not found': 'Failed to connect to Freshdesk. Make sure your domain is correct.',
        'invalid api key': 'Failed to connect to Freshdesk. Make sure your API Key is correct.',
      },
    });

    if (error) {
      setLoading(false);
      showToast(error.message, ToastTypes.error);
      return false;
    }

    await reloadFreshdeskSettings();
    setLoading(false);
    return true;
  };

  const footer = (
    <>
      <ButtonV2 variant="outlined" size="medium" onClick={onClose}>
        Cancel
      </ButtonV2>
      <ButtonV2
        size="medium"
        loading={loading}
        onClick={onEnableClick}
        disabled={!isValid && !isInstalled}
        className={styles.connectButton}>
        Connect
      </ButtonV2>
    </>
  );

  const header = (
    <div className={styles.modalHeader}>
      <div className={styles.cannyLogoContainer}>
        <LazyLoadedImage src={integration.icon} className={styles.integrationLogo} alt="" />
      </div>
      <div className={styles.lineContainer}>
        <span className={styles.line} />
        <span className={styles.line} />
      </div>
      <div className={styles.cannyLogoContainer}>
        <LazyLoadedImage src={CannyLogo} className={styles.cannyLogo} alt="" />
      </div>
    </div>
  );

  return (
    <ModernModal
      modalClassName={styles.modal}
      header={header}
      onClose={onClose}
      footer={footer}
      sections={[
        <>
          <H2 variant="headingXl" className={styles.heading}>
            Connect Freshdesk to Autopilot
          </H2>
          <P className={styles.subheading}>
            Enable Autopilot to capture user feedback found in freshdesk tickets.
          </P>
          {!isInstalled && (
            <div className={styles.freshdeskInputs}>
              <Label
                className={styles.freshdeskLabel}
                label={<P fontWeight="medium">Domain</P>}
                labelPlacement="top">
                <TextInput
                  onChange={(e) => setDomain(e.currentTarget.value)}
                  placeholder="company.freshdesk.com"
                  value={domain}
                />
              </Label>
              <Label
                className={styles.freshdeskLabel}
                label={<P fontWeight="medium">API Key</P>}
                labelPlacement="top">
                <TextInput
                  onChange={(e) => setAPIKey(e.currentTarget.value)}
                  placeholder="e.g. rZmPxkVTqWoLjdCNis"
                  value={apiKey}
                />
              </Label>
            </div>
          )}
          <AdminAutopilotInfoBox points={InfoPoints[SyncIntegrationNames.freshdesk] ?? []} />
        </>,
      ]}
    />
  );
};

export default compose(connect(null, { reloadFreshdeskSettings }))(
  FreshdeskIntegrationModal,
) as unknown as React.FC<OwnProps>;
